// src/components/admin/nornsweave/SettingsTab.js
import React, { useState } from 'react';
import { useAdminNornsWeave } from './AdminNornsWeaveContext';
import TimePicker, { formatTime12 } from '../../TimePicker';
import { sendBookingEmail } from '../../../utils/emailService';

function SettingsTab({
  onSettingsSave,
  onCredentialsSave,
  onAddBlock,
  onRemoveBlock,
  onAddTeacher,
  onDeleteTeacher,
  autoSaveSettings,
  showNotification
}) {
  const {
    settingsForm,
    setSettingsForm,
    teachers,
    aides,
    isValidEmail
  } = useAdminNornsWeave();

  const [showPassword, setShowPassword] = useState(false);
  const [newTeacher, setNewTeacher] = useState({
    name: '',
    email: '',
    grade: '',
    subject: '',
    role: 'teacher',
    departments: []
  });

  // New contact/chair form states
  const [newAdminContact, setNewAdminContact] = useState({ name: '', email: '', notifyOn: ['closures'] });
  const [newLibraryChair, setNewLibraryChair] = useState({ name: '', email: '' });

  // Test email states
  const [expandedTestEmail, setExpandedTestEmail] = useState(null);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailResults, setTestEmailResults] = useState({});
  const [testForms, setTestForms] = useState({
    closure_teacher: { teacherName: '', grade: '3rd Grade', date: new Date().toISOString().split('T')[0], startTime: '09:00', endTime: '10:00', library: 'elementary', reason: 'sick_day', customReason: '' },
    closure_aide: { aideName: '', dayType: 'A', date: new Date().toISOString().split('T')[0], reason: 'sick_day', customReason: '', workAssignment: '', reportTo: '' },
    closure_library_assistant: { date: new Date().toISOString().split('T')[0], dateEnd: new Date().toISOString().split('T')[0], reason: 'tla_conference', customReason: '', library: 'both' },
    closure_library_chairs: { date: new Date().toISOString().split('T')[0], reason: 'sick_day', customReason: '', library: 'both', affectedClassCount: 5, customMessage: '' },
    closure_admin_cc: { date: new Date().toISOString().split('T')[0], dateEnd: new Date().toISOString().split('T')[0], reason: 'professional_development', customReason: '', library: 'both', timeType: 'full', startTime: '', endTime: '', affectedClassCount: 8, customMessage: '' },
    recess_cancellation: { teacherName: '', date: new Date().toISOString().split('T')[0], reason: 'professional_development', customReason: '' },
    booking_request: { teacherName: '', date: new Date().toISOString().split('T')[0], startTime: '10:00', endTime: '11:00', library: 'elementary', grade: '2nd Grade' },
    booking_approved: { teacherName: '', date: new Date().toISOString().split('T')[0], startTime: '10:00', endTime: '11:00', library: 'elementary', grade: '2nd Grade' },
    booking_declined: { teacherName: '', date: new Date().toISOString().split('T')[0], startTime: '10:00', endTime: '11:00', library: 'elementary', grade: '2nd Grade', rejectionReason: '', suggestedAlternatives: '' },
    booking_reminder: { teacherName: '', date: new Date(Date.now() + 86400000).toISOString().split('T')[0], startTime: '10:00', endTime: '11:00', library: 'elementary', grade: '2nd Grade' },
    teacher_late_cancel: { teacherName: '', grade: '4th Grade', subject: '', date: new Date(Date.now() + 172800000).toISOString().split('T')[0], startTime: '09:00', endTime: '10:00', library: 'elementary', daysNotice: 2 },
    weekly_digest: { weekStart: '' },
    book_fair: { teacherName: '', startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 432000000).toISOString().split('T')[0] },
    holiday_single: { teacherName: '', holidayName: 'Martin Luther King Jr. Day', holidayDate: new Date().toISOString().split('T')[0], affectedClass: '3rd Grade - Mondays 10:00 AM' },
    holiday_break: { teacherName: '', holidayName: 'Christmas Break', startDate: '2025-12-20', endDate: '2026-01-03', returnDate: '2026-01-06' }
  });

  // Collapsible section states (all collapsed by default)
  const [expandedSections, setExpandedSections] = useState({
    portalAccess: false,
    libraryHours: false,
    scheduleBlocks: false,
    emailNotifications: false,
    recessConfig: false,
    adminContacts: false,
    libraryChairs: false,
    libraryAssistant: false,
    testEmails: false,
    teacherDirectory: false
  });

  // Grade options for recess configuration
  const RECESS_GRADE_OPTIONS = ['PreK', 'K', '1', '2', '3', '4', '5', '6', '7', '8'];

  // Day options for recess
  const DAY_OPTIONS = [
    { value: 'everyday', label: 'Every Day (Mon-Fri)' },
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' }
  ];

  // Closure reason options for test email forms
  const CLOSURE_REASON_OPTIONS = [
    { value: 'sick_day', label: 'Sick Day' },
    { value: 'professional_development', label: 'Professional Development' },
    { value: 'tla_conference', label: 'TLA Conference' },
    { value: 'faculty_meeting', label: 'Faculty Meeting' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'weather', label: 'Weather' },
    { value: 'other', label: 'Other' }
  ];

  // Helper to get reason label from value
  const getReasonLabel = (value) => {
    const option = CLOSURE_REASON_OPTIONS.find(o => o.value === value);
    return option ? option.label : value;
  };

  // Helper to update test form values
  const updateTestForm = (emailId, field, value) => {
    setTestForms(prev => ({
      ...prev,
      [emailId]: { ...prev[emailId], [field]: value }
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const ROLE_OPTIONS = [
    { value: 'teacher', label: 'Teacher' },
    { value: 'aide', label: 'Aide' },
    { value: 'admin', label: 'Admin' },
    { value: 'specialist', label: 'Specialist' },
    { value: 'substitute', label: 'Substitute' }
  ];

  const DEPARTMENT_OPTIONS = [
    { value: 'elementary', label: 'Elementary' },
    { value: 'middle', label: 'Middle School' },
    { value: 'special_ed', label: 'Special Ed' },
    { value: 'specials', label: 'Specials' }
  ];

  const toggleDepartment = (dept) => {
    setNewTeacher(prev => ({
      ...prev,
      departments: prev.departments.includes(dept)
        ? prev.departments.filter(d => d !== dept)
        : [...prev.departments, dept]
    }));
  };

  // Helper to get next Monday
  const getNextMonday = (date) => {
    const result = new Date(date);
    const day = result.getDay();
    const daysUntilMonday = day === 0 ? 1 : (8 - day);
    result.setDate(result.getDate() + daysUntilMonday);
    return result;
  };

  // Common input styles for test email forms
  const inputClass = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-200 outline-none";
  const selectClass = "w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-200 outline-none";
  const labelClass = "block text-slate-700 text-xs font-medium mb-1";

  // Test email types configuration with forms
  const TEST_EMAIL_TYPES = [
    {
      id: 'closure_teacher',
      name: 'Closure - Teacher Notification',
      description: 'Sent to teachers when their class is cancelled due to closure',
      icon: '🚫',
      category: 'Closures',
      getRecipients: () => ['Teachers with bookings on closure date'],
      renderForm: () => {
        const form = testForms.closure_teacher;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('closure_teacher', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Grade</label>
              <input type="text" className={inputClass} placeholder="3rd Grade" value={form.grade} onChange={(e) => updateTestForm('closure_teacher', 'grade', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('closure_teacher', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Time</label>
              <input type="time" className={inputClass} value={form.startTime} onChange={(e) => updateTestForm('closure_teacher', 'startTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Time</label>
              <input type="time" className={inputClass} value={form.endTime} onChange={(e) => updateTestForm('closure_teacher', 'endTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('closure_teacher', 'library', e.target.value)}>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Reason</label>
              <select className={selectClass} value={form.reason} onChange={(e) => updateTestForm('closure_teacher', 'reason', e.target.value)}>
                {CLOSURE_REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {form.reason === 'other' && (
              <div className="col-span-2">
                <label className={labelClass}>Custom Reason</label>
                <input type="text" className={inputClass} placeholder="Enter reason..." value={form.customReason} onChange={(e) => updateTestForm('closure_teacher', 'customReason', e.target.value)} />
              </div>
            )}
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.closure_teacher;
        const reason = form.reason === 'other' ? form.customReason : getReasonLabel(form.reason);
        return {
          type: 'cancellation',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          bookingDate: form.date,
          bookingTime: `${form.startTime} - ${form.endTime}`,
          library: form.library,
          grade: form.grade,
          cancellationReason: `Library Closure - ${reason}`,
        };
      },
    },
    {
      id: 'closure_aide',
      name: 'Closure - Aide Notification',
      description: 'Sent to aides when library closes on their scheduled day',
      icon: '🙋',
      category: 'Closures',
      getRecipients: () => {
        const aidesA = (aides || []).filter(a => a.dayType === 'A').map(a => a.name);
        const aidesB = (aides || []).filter(a => a.dayType === 'B').map(a => a.name);
        return [`A Day: ${aidesA.length > 0 ? aidesA.join(', ') : 'None'}`, `B Day: ${aidesB.length > 0 ? aidesB.join(', ') : 'None'}`];
      },
      renderForm: () => {
        const form = testForms.closure_aide;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Aide Name</label>
              <input type="text" className={inputClass} placeholder="John Doe" value={form.aideName} onChange={(e) => updateTestForm('closure_aide', 'aideName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Day Type</label>
              <select className={selectClass} value={form.dayType} onChange={(e) => updateTestForm('closure_aide', 'dayType', e.target.value)}>
                <option value="A">A Day</option>
                <option value="B">B Day</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('closure_aide', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Reason</label>
              <select className={selectClass} value={form.reason} onChange={(e) => updateTestForm('closure_aide', 'reason', e.target.value)}>
                {CLOSURE_REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {form.reason === 'other' && (
              <div>
                <label className={labelClass}>Custom Reason</label>
                <input type="text" className={inputClass} placeholder="Enter reason..." value={form.customReason} onChange={(e) => updateTestForm('closure_aide', 'customReason', e.target.value)} />
              </div>
            )}
            <div>
              <label className={labelClass}>Work Assignment</label>
              <input type="text" className={inputClass} placeholder="Shelf reading in fiction section" value={form.workAssignment} onChange={(e) => updateTestForm('closure_aide', 'workAssignment', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Report To</label>
              <input type="text" className={inputClass} placeholder="Front office" value={form.reportTo} onChange={(e) => updateTestForm('closure_aide', 'reportTo', e.target.value)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.closure_aide;
        const reason = form.reason === 'other' ? form.customReason : getReasonLabel(form.reason);
        return {
          type: 'closure_aide_notification',
          aideName: form.aideName || 'Test Aide',
          dayType: form.dayType,
          closureDate: form.date,
          closureReason: reason,
          aideWorkAssignment: form.workAssignment || 'Shelf reading in fiction section',
          aideReportTo: form.reportTo || 'Front office',
        };
      },
    },
    {
      id: 'closure_library_assistant',
      name: 'Closure - Library Assistant',
      description: 'Sent to library assistant for full-day closures',
      icon: '👩‍💼',
      category: 'Closures',
      getRecipients: () => {
        const assistant = settingsForm.libraryAssistant;
        if (assistant?.enabled && assistant?.name) {
          return [`${assistant.name} (${assistant.email})`];
        }
        return ['Not configured'];
      },
      renderForm: () => {
        const form = testForms.closure_library_assistant;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Start Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('closure_library_assistant', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Date</label>
              <input type="date" className={inputClass} value={form.dateEnd} onChange={(e) => updateTestForm('closure_library_assistant', 'dateEnd', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('closure_library_assistant', 'library', e.target.value)}>
                <option value="both">Both Libraries</option>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Reason</label>
              <select className={selectClass} value={form.reason} onChange={(e) => updateTestForm('closure_library_assistant', 'reason', e.target.value)}>
                {CLOSURE_REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {form.reason === 'other' && (
              <div className="col-span-2">
                <label className={labelClass}>Custom Reason</label>
                <input type="text" className={inputClass} placeholder="Enter reason..." value={form.customReason} onChange={(e) => updateTestForm('closure_library_assistant', 'customReason', e.target.value)} />
              </div>
            )}
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.closure_library_assistant;
        const reason = form.reason === 'other' ? form.customReason : getReasonLabel(form.reason);
        return {
          type: 'closure_library_assistant',
          assistantName: settingsForm.libraryAssistant?.name || 'Library Assistant',
          closureDate: form.date,
          closureDateEnd: form.dateEnd,
          closureReason: reason,
          library: form.library,
        };
      },
    },
    {
      id: 'closure_library_chairs',
      name: 'Closure - Library Chairs (Emergency)',
      description: 'Sent to library chairs for emergency/immediate closures',
      icon: '🚨',
      category: 'Closures',
      getRecipients: () => {
        const chairs = settingsForm.libraryChairs || [];
        if (chairs.length === 0) return ['No library chairs configured'];
        return chairs.map(c => `${c.name} (${c.email})`);
      },
      renderForm: () => {
        const form = testForms.closure_library_chairs;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('closure_library_chairs', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('closure_library_chairs', 'library', e.target.value)}>
                <option value="both">Both Libraries</option>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Affected Classes</label>
              <input type="number" className={inputClass} min="0" value={form.affectedClassCount} onChange={(e) => updateTestForm('closure_library_chairs', 'affectedClassCount', parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <label className={labelClass}>Reason</label>
              <select className={selectClass} value={form.reason} onChange={(e) => updateTestForm('closure_library_chairs', 'reason', e.target.value)}>
                {CLOSURE_REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {form.reason === 'other' && (
              <div>
                <label className={labelClass}>Custom Reason</label>
                <input type="text" className={inputClass} placeholder="Enter reason..." value={form.customReason} onChange={(e) => updateTestForm('closure_library_chairs', 'customReason', e.target.value)} />
              </div>
            )}
            <div className="col-span-2 md:col-span-3">
              <label className={labelClass}>Custom Message (optional)</label>
              <input type="text" className={inputClass} placeholder="Apologies for the short notice..." value={form.customMessage} onChange={(e) => updateTestForm('closure_library_chairs', 'customMessage', e.target.value)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.closure_library_chairs;
        const reason = form.reason === 'other' ? form.customReason : getReasonLabel(form.reason);
        return {
          type: 'closure_library_chairs',
          chairName: 'Library Chair',
          closureDate: form.date,
          closureReason: reason,
          library: form.library,
          affectedClassCount: form.affectedClassCount,
          customMessage: form.customMessage,
        };
      },
    },
    {
      id: 'closure_admin_cc',
      name: 'Closure - Admin CC',
      description: 'CC notification sent to admin contacts',
      icon: '📋',
      category: 'Closures',
      getRecipients: () => {
        const contacts = (settingsForm.adminContacts || []).filter(c => c.notifyOn?.includes('closures'));
        if (contacts.length === 0) return ['No admin contacts configured for closures'];
        return contacts.map(c => `${c.name} (${c.email})`);
      },
      renderForm: () => {
        const form = testForms.closure_admin_cc;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Start Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('closure_admin_cc', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Date</label>
              <input type="date" className={inputClass} value={form.dateEnd} onChange={(e) => updateTestForm('closure_admin_cc', 'dateEnd', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('closure_admin_cc', 'library', e.target.value)}>
                <option value="both">Both Libraries</option>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Time Type</label>
              <select className={selectClass} value={form.timeType} onChange={(e) => updateTestForm('closure_admin_cc', 'timeType', e.target.value)}>
                <option value="full">Full Day</option>
                <option value="partial">Partial Day</option>
              </select>
            </div>
            {form.timeType === 'partial' && (
              <>
                <div>
                  <label className={labelClass}>Start Time</label>
                  <input type="time" className={inputClass} value={form.startTime} onChange={(e) => updateTestForm('closure_admin_cc', 'startTime', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>End Time</label>
                  <input type="time" className={inputClass} value={form.endTime} onChange={(e) => updateTestForm('closure_admin_cc', 'endTime', e.target.value)} />
                </div>
              </>
            )}
            <div>
              <label className={labelClass}>Affected Classes</label>
              <input type="number" className={inputClass} min="0" value={form.affectedClassCount} onChange={(e) => updateTestForm('closure_admin_cc', 'affectedClassCount', parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <label className={labelClass}>Reason</label>
              <select className={selectClass} value={form.reason} onChange={(e) => updateTestForm('closure_admin_cc', 'reason', e.target.value)}>
                {CLOSURE_REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {form.reason === 'other' && (
              <div>
                <label className={labelClass}>Custom Reason</label>
                <input type="text" className={inputClass} placeholder="Enter reason..." value={form.customReason} onChange={(e) => updateTestForm('closure_admin_cc', 'customReason', e.target.value)} />
              </div>
            )}
            <div className="col-span-2 md:col-span-3">
              <label className={labelClass}>Custom Message (optional)</label>
              <input type="text" className={inputClass} placeholder="Additional notes..." value={form.customMessage} onChange={(e) => updateTestForm('closure_admin_cc', 'customMessage', e.target.value)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.closure_admin_cc;
        const reason = form.reason === 'other' ? form.customReason : getReasonLabel(form.reason);
        return {
          type: 'closure_admin_cc',
          contactName: 'Admin Contact',
          closureDate: form.date,
          closureDateEnd: form.dateEnd,
          closureReason: reason,
          library: form.library,
          timeType: form.timeType,
          startTime: form.startTime,
          endTime: form.endTime,
          affectedClassCount: form.affectedClassCount,
          customMessage: form.customMessage,
        };
      },
    },
    {
      id: 'recess_cancellation',
      name: 'Recess Cancellation',
      description: 'Sent to teachers when library recess is cancelled (both libraries closed)',
      icon: '🏃',
      category: 'Closures',
      getRecipients: () => {
        const schedules = (settingsForm.recessSchedules || []).filter(r => r.enabled);
        if (schedules.length === 0) return ['No recesses configured'];
        const allGrades = [...new Set(schedules.flatMap(r => r.grades || []))];
        const recessTeachers = teachers.filter(t => allGrades.includes(t.grade));
        if (recessTeachers.length === 0) return [`No teachers in grades ${allGrades.join(', ')}`];
        return schedules.map(r => `${r.description || 'Recess'}: grades ${(r.grades || []).join(', ')}`);
      },
      renderForm: () => {
        const form = testForms.recess_cancellation;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('recess_cancellation', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('recess_cancellation', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Reason</label>
              <select className={selectClass} value={form.reason} onChange={(e) => updateTestForm('recess_cancellation', 'reason', e.target.value)}>
                {CLOSURE_REASON_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            {form.reason === 'other' && (
              <div className="col-span-2">
                <label className={labelClass}>Custom Reason</label>
                <input type="text" className={inputClass} placeholder="Enter reason..." value={form.customReason} onChange={(e) => updateTestForm('recess_cancellation', 'customReason', e.target.value)} />
              </div>
            )}
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.recess_cancellation;
        const reason = form.reason === 'other' ? form.customReason : getReasonLabel(form.reason);
        return {
          type: 'recess_cancellation',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          closureDate: form.date,
          closureReason: reason,
          recessStartTime: (settingsForm.recessSchedules || [])[0]?.startTime || '11:30',
          recessEndTime: (settingsForm.recessSchedules || [])[0]?.endTime || '12:00',
        };
      },
    },
    {
      id: 'booking_request',
      name: 'Booking Request Submitted',
      description: 'Sent to teacher when they submit a booking request',
      icon: '📝',
      category: 'Bookings',
      getRecipients: () => ['Teacher who submitted the request'],
      renderForm: () => {
        const form = testForms.booking_request;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('booking_request', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Grade</label>
              <input type="text" className={inputClass} placeholder="2nd Grade" value={form.grade} onChange={(e) => updateTestForm('booking_request', 'grade', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('booking_request', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Time</label>
              <input type="time" className={inputClass} value={form.startTime} onChange={(e) => updateTestForm('booking_request', 'startTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Time</label>
              <input type="time" className={inputClass} value={form.endTime} onChange={(e) => updateTestForm('booking_request', 'endTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('booking_request', 'library', e.target.value)}>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.booking_request;
        return {
          type: 'request_submitted',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          bookingDate: form.date,
          bookingTime: `${form.startTime} - ${form.endTime}`,
          library: form.library,
          grade: form.grade,
        };
      },
    },
    {
      id: 'booking_approved',
      name: 'Booking Approved',
      description: 'Sent to teacher when their booking is approved',
      icon: '✅',
      category: 'Bookings',
      getRecipients: () => ['Teacher whose booking was approved'],
      renderForm: () => {
        const form = testForms.booking_approved;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('booking_approved', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Grade</label>
              <input type="text" className={inputClass} placeholder="2nd Grade" value={form.grade} onChange={(e) => updateTestForm('booking_approved', 'grade', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('booking_approved', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Time</label>
              <input type="time" className={inputClass} value={form.startTime} onChange={(e) => updateTestForm('booking_approved', 'startTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Time</label>
              <input type="time" className={inputClass} value={form.endTime} onChange={(e) => updateTestForm('booking_approved', 'endTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('booking_approved', 'library', e.target.value)}>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.booking_approved;
        return {
          type: 'approved',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          bookingDate: form.date,
          bookingTime: `${form.startTime} - ${form.endTime}`,
          library: form.library,
          grade: form.grade,
        };
      },
    },
    {
      id: 'booking_declined',
      name: 'Booking Declined',
      description: 'Sent to teacher when their booking is declined',
      icon: '❌',
      category: 'Bookings',
      getRecipients: () => ['Teacher whose booking was declined'],
      renderForm: () => {
        const form = testForms.booking_declined;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('booking_declined', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Grade</label>
              <input type="text" className={inputClass} placeholder="2nd Grade" value={form.grade} onChange={(e) => updateTestForm('booking_declined', 'grade', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('booking_declined', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Time</label>
              <input type="time" className={inputClass} value={form.startTime} onChange={(e) => updateTestForm('booking_declined', 'startTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Time</label>
              <input type="time" className={inputClass} value={form.endTime} onChange={(e) => updateTestForm('booking_declined', 'endTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('booking_declined', 'library', e.target.value)}>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className={labelClass}>Rejection Reason</label>
              <input type="text" className={inputClass} placeholder="Time slot unavailable due to scheduled event" value={form.rejectionReason} onChange={(e) => updateTestForm('booking_declined', 'rejectionReason', e.target.value)} />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className={labelClass}>Suggested Alternatives</label>
              <input type="text" className={inputClass} placeholder="Tuesday 10:00 AM or Wednesday 2:00 PM" value={form.suggestedAlternatives} onChange={(e) => updateTestForm('booking_declined', 'suggestedAlternatives', e.target.value)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.booking_declined;
        return {
          type: 'declined',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          bookingDate: form.date,
          bookingTime: `${form.startTime} - ${form.endTime}`,
          library: form.library,
          grade: form.grade,
          rejectionReason: form.rejectionReason || 'Time slot unavailable due to scheduled event',
          suggestedAlternatives: form.suggestedAlternatives || 'Tuesday 10:00 AM or Wednesday 2:00 PM',
        };
      },
    },
    {
      id: 'booking_reminder',
      name: 'Booking Reminder',
      description: 'Sent to teacher the day before their scheduled visit',
      icon: '⏰',
      category: 'Bookings',
      getRecipients: () => ['Teachers with bookings tomorrow'],
      renderForm: () => {
        const form = testForms.booking_reminder;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('booking_reminder', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Grade</label>
              <input type="text" className={inputClass} placeholder="2nd Grade" value={form.grade} onChange={(e) => updateTestForm('booking_reminder', 'grade', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('booking_reminder', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Time</label>
              <input type="time" className={inputClass} value={form.startTime} onChange={(e) => updateTestForm('booking_reminder', 'startTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Time</label>
              <input type="time" className={inputClass} value={form.endTime} onChange={(e) => updateTestForm('booking_reminder', 'endTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('booking_reminder', 'library', e.target.value)}>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.booking_reminder;
        return {
          type: 'reminder',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          bookingDate: form.date,
          bookingTime: `${form.startTime} - ${form.endTime}`,
          library: form.library,
          grade: form.grade,
        };
      },
    },
    {
      id: 'teacher_late_cancel',
      name: 'Late Teacher Cancellation',
      description: 'Sent to library chairs when a teacher cancels with short notice',
      icon: '⚠️',
      category: 'Library Chairs',
      getRecipients: () => {
        const chairs = settingsForm.libraryChairs || [];
        if (chairs.length === 0) return ['No library chairs configured'];
        if (!settingsForm.libraryChairSettings?.notifyOnLateTeacherCancel) return ['Late cancel notifications disabled'];
        return chairs.map(c => `${c.name} (${c.email})`);
      },
      renderForm: () => {
        const form = testForms.teacher_late_cancel;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('teacher_late_cancel', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Grade</label>
              <input type="text" className={inputClass} placeholder="4th Grade" value={form.grade} onChange={(e) => updateTestForm('teacher_late_cancel', 'grade', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Subject (optional)</label>
              <input type="text" className={inputClass} placeholder="English" value={form.subject} onChange={(e) => updateTestForm('teacher_late_cancel', 'subject', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Class Date</label>
              <input type="date" className={inputClass} value={form.date} onChange={(e) => updateTestForm('teacher_late_cancel', 'date', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Time</label>
              <input type="time" className={inputClass} value={form.startTime} onChange={(e) => updateTestForm('teacher_late_cancel', 'startTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Time</label>
              <input type="time" className={inputClass} value={form.endTime} onChange={(e) => updateTestForm('teacher_late_cancel', 'endTime', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select className={selectClass} value={form.library} onChange={(e) => updateTestForm('teacher_late_cancel', 'library', e.target.value)}>
                <option value="elementary">Elementary</option>
                <option value="middle">Middle School</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Days Notice</label>
              <input type="number" className={inputClass} min="0" max="30" value={form.daysNotice} onChange={(e) => updateTestForm('teacher_late_cancel', 'daysNotice', parseInt(e.target.value) || 0)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.teacher_late_cancel;
        return {
          type: 'teacher_late_cancellation',
          grade: form.grade,
          subject: form.subject,
          teacherName: form.teacherName || 'Mrs. Smith',
          classDate: form.date,
          classTime: `${form.startTime} - ${form.endTime}`,
          library: form.library,
          daysNotice: form.daysNotice,
        };
      },
    },
    {
      id: 'weekly_digest',
      name: 'Weekly Digest',
      description: "Sent to library chairs every Friday with next week's schedule",
      icon: '📅',
      category: 'Library Chairs',
      getRecipients: () => {
        const chairs = settingsForm.libraryChairs || [];
        if (chairs.length === 0) return ['No library chairs configured'];
        if (!settingsForm.libraryChairSettings?.weeklyDigestEnabled) return ['Weekly digest disabled'];
        return chairs.map(c => `${c.name} (${c.email})`);
      },
      renderForm: () => {
        return (
          <div className="text-sm text-slate-600 italic">
            Weekly digest uses sample schedule data. No customization needed.
          </div>
        );
      },
      buildEmailData: () => {
        return {
          type: 'weekly_digest',
          chairName: 'Library Chair',
          weekStart: getNextMonday(new Date()).toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' }),
          elementarySchedule: [
            { date: getNextMonday(new Date()).toISOString().split('T')[0], dayName: 'Monday', dateShort: 'Mon', classes: [{ time: '10:00 AM - 11:00 AM', grade: '2nd Grade', teacher: 'Mrs. Johnson', type: 'fixed', status: 'fixed' }] },
          ],
          middleSchedule: [
            { date: getNextMonday(new Date()).toISOString().split('T')[0], dayName: 'Monday', dateShort: 'Mon', classes: [{ time: '9:00 AM - 10:00 AM', grade: '6th English', teacher: 'Ms. Martinez', type: 'booking', status: 'approved' }] },
          ],
          notes: ['Book Fair in progress'],
        };
      },
    },
    {
      id: 'book_fair',
      name: 'Book Fair Announcement',
      description: 'Sent to all teachers when Book Fair is coming up',
      icon: '📖',
      category: 'Events',
      getRecipients: () => [`All ${teachers.length} teachers`],
      renderForm: () => {
        const form = testForms.book_fair;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('book_fair', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Date</label>
              <input type="date" className={inputClass} value={form.startDate} onChange={(e) => updateTestForm('book_fair', 'startDate', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Date</label>
              <input type="date" className={inputClass} value={form.endDate} onChange={(e) => updateTestForm('book_fair', 'endDate', e.target.value)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.book_fair;
        return {
          type: 'book_fair_announcement',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          startDate: form.startDate,
          endDate: form.endDate,
        };
      },
    },
    {
      id: 'holiday_single',
      name: 'Holiday - Single Day',
      description: 'Sent to affected teachers for single-day holidays',
      icon: '🏖️',
      category: 'Events',
      getRecipients: () => ['Teachers with fixed classes on the holiday'],
      renderForm: () => {
        const form = testForms.holiday_single;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('holiday_single', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Holiday Name</label>
              <input type="text" className={inputClass} placeholder="Martin Luther King Jr. Day" value={form.holidayName} onChange={(e) => updateTestForm('holiday_single', 'holidayName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Holiday Date</label>
              <input type="date" className={inputClass} value={form.holidayDate} onChange={(e) => updateTestForm('holiday_single', 'holidayDate', e.target.value)} />
            </div>
            <div className="col-span-2 md:col-span-3">
              <label className={labelClass}>Affected Class</label>
              <input type="text" className={inputClass} placeholder="3rd Grade - Mondays 10:00 AM" value={form.affectedClass} onChange={(e) => updateTestForm('holiday_single', 'affectedClass', e.target.value)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.holiday_single;
        return {
          type: 'holiday_single_day',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          holidayName: form.holidayName,
          holidayDate: form.holidayDate,
          affectedClass: form.affectedClass,
        };
      },
    },
    {
      id: 'holiday_break',
      name: 'Holiday - Multi-Day Break',
      description: 'Sent to all fixed class teachers for multi-day breaks',
      icon: '🎄',
      category: 'Events',
      getRecipients: () => ['All teachers with fixed classes'],
      renderForm: () => {
        const form = testForms.holiday_break;
        return (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Teacher Name</label>
              <input type="text" className={inputClass} placeholder="Mrs. Smith" value={form.teacherName} onChange={(e) => updateTestForm('holiday_break', 'teacherName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Holiday Name</label>
              <input type="text" className={inputClass} placeholder="Christmas Break" value={form.holidayName} onChange={(e) => updateTestForm('holiday_break', 'holidayName', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Start Date</label>
              <input type="date" className={inputClass} value={form.startDate} onChange={(e) => updateTestForm('holiday_break', 'startDate', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>End Date</label>
              <input type="date" className={inputClass} value={form.endDate} onChange={(e) => updateTestForm('holiday_break', 'endDate', e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Return Date</label>
              <input type="date" className={inputClass} value={form.returnDate} onChange={(e) => updateTestForm('holiday_break', 'returnDate', e.target.value)} />
            </div>
          </div>
        );
      },
      buildEmailData: () => {
        const form = testForms.holiday_break;
        return {
          type: 'holiday_break',
          teacherName: form.teacherName || settingsForm.yourName || 'Test Teacher',
          holidayName: form.holidayName,
          startDate: form.startDate,
          endDate: form.endDate,
          returnDate: form.returnDate,
        };
      },
    },
  ];

  // Send a test email
  const sendTestEmail = async (emailType) => {
    if (!settingsForm.librarianEmail) {
      showNotification('Please configure your email address first', 'error');
      return;
    }

    setSendingTestEmail(true);

    try {
      // Use buildEmailData if available, otherwise fall back to getSampleData (for backwards compatibility)
      const emailTypeData = emailType.buildEmailData ? emailType.buildEmailData() : emailType.getSampleData?.();

      // Add common fields
      const emailData = {
        ...emailTypeData,
        to: settingsForm.librarianEmail,
        librarianName: settingsForm.yourName || 'Librarian',
        schoolName: settingsForm.schoolName || 'School',
        replyTo: settingsForm.librarianEmail,
        isTest: true,
      };

      const response = await sendBookingEmail(emailData);

      if (response.success) {
        setTestEmailResults(prev => ({ ...prev, [emailType.id]: 'success' }));
        showNotification(`Test email sent to ${settingsForm.librarianEmail}`, 'success');
      } else {
        setTestEmailResults(prev => ({ ...prev, [emailType.id]: 'error' }));
        showNotification(`Failed to send: ${response.error}`, 'error');
      }
    } catch (error) {
      console.error('Test email error:', error);
      setTestEmailResults(prev => ({ ...prev, [emailType.id]: 'error' }));
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setSendingTestEmail(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-2xl font-bold text-slate-800 mb-6">Norns' Weave Settings</h2>
      <form onSubmit={onSettingsSave} className="space-y-6">
        {/* Teacher Portal Access */}
        <div className="border-2 border-purple-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-purple-50 to-indigo-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-purple-100 hover:to-indigo-100"
            onClick={() => toggleSection('portalAccess')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.portalAccess ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-slate-800">Teacher Portal Access</h3>
            </div>
          </div>
          {expandedSections.portalAccess && (
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 px-6 pb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">School Name</label>
              <input
                type="text"
                required
                placeholder="e.g., Holy Family Catholic School"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                value={settingsForm.schoolName}
                onChange={(e) => setSettingsForm({...settingsForm, schoolName: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">City Name</label>
              <input
                type="text"
                required
                placeholder="e.g., Austin"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                value={settingsForm.cityName}
                onChange={(e) => setSettingsForm({...settingsForm, cityName: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Your Name</label>
              <input
                type="text"
                required
                placeholder="e.g., Kahn"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                value={settingsForm.yourName}
                onChange={(e) => setSettingsForm({...settingsForm, yourName: e.target.value})}
              />
            </div>
          </div>

          {settingsForm.schoolName && settingsForm.cityName && settingsForm.yourName && (
            <div className="mb-4 p-4 bg-purple-100 border border-purple-300 rounded-lg">
              <p className="text-sm text-purple-700 font-medium mb-1">Generated Access Code:</p>
              <p className="text-lg font-bold text-purple-900 font-mono">
                {`${settingsForm.schoolName.split(/\s+/)[0]}${settingsForm.cityName}${settingsForm.yourName.trim().split(/\s+/).pop()}`}
              </p>
              <p className="text-xs text-purple-600 mt-2">Teachers access the portal at: <strong>{window.location.origin}/calendar</strong></p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-slate-700 text-sm font-medium mb-1">Teacher Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:border-purple-500 focus:ring-2 focus:ring-purple-200 outline-none"
                  value={settingsForm.password}
                  onChange={(e) => setSettingsForm({...settingsForm, password: e.target.value})}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
            </div>

            {settingsForm.schoolName && settingsForm.cityName && settingsForm.yourName && settingsForm.password && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    const accessCode = `${settingsForm.schoolName.split(/\s+/)[0]}${settingsForm.cityName}${settingsForm.yourName.trim().split(/\s+/).pop()}`;
                    const text = `Library Booking Portal\n\nURL: ${window.location.origin}/calendar\nAccess Code: ${accessCode}\nPassword: ${settingsForm.password}\n\nUse these credentials to book library time for your class.`;
                    navigator.clipboard.writeText(text);
                    showNotification('Credentials copied to clipboard!', 'success');
                  }}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                >
                  Copy Credentials
                </button>
                <button
                  type="button"
                  onClick={onCredentialsSave}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  Save Credentials
                </button>
              </div>
            )}
          </div>
          </div>
          )}
        </div>

        {/* Library Hours */}
        <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
          <div
            className="bg-slate-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-slate-100"
            onClick={() => toggleSection('libraryHours')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.libraryHours ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-slate-800">Library Hours</h3>
            </div>
          </div>
          {expandedSections.libraryHours && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                <h3 className="font-bold text-purple-700 mb-4">Elementary Library Hours</h3>
                <div className="space-y-4">
                  <TimePicker
                    label="Start Time"
                    value={settingsForm.elementaryStart}
                    onChange={(val) => {
                      const newForm = {...settingsForm, elementaryStart: val};
                      setSettingsForm(newForm);
                      autoSaveSettings(newForm);
                    }}
                    required
                  />
                  <TimePicker
                    label="End Time"
                    value={settingsForm.elementaryEnd}
                    onChange={(val) => {
                      const newForm = {...settingsForm, elementaryEnd: val};
                      setSettingsForm(newForm);
                      autoSaveSettings(newForm);
                    }}
                    required
                  />
                </div>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6">
                <h3 className="font-bold text-indigo-700 mb-4">Middle School Library Hours</h3>
                <div className="space-y-4">
                  <TimePicker
                    label="Start Time"
                    value={settingsForm.middleSchoolStart}
                    onChange={(val) => {
                      const newForm = {...settingsForm, middleSchoolStart: val};
                      setSettingsForm(newForm);
                      autoSaveSettings(newForm);
                    }}
                    required
                  />
                  <TimePicker
                    label="End Time"
                    value={settingsForm.middleSchoolEnd}
                    onChange={(val) => {
                      const newForm = {...settingsForm, middleSchoolEnd: val};
                      setSettingsForm(newForm);
                      autoSaveSettings(newForm);
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Schedule Blocks */}
        <div className="border-2 border-gray-200 rounded-xl overflow-hidden">
          <div
            className="bg-gray-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:bg-gray-100"
            onClick={() => toggleSection('scheduleBlocks')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.scheduleBlocks ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-slate-800">Your Schedule (Blocks)</h3>
              <span className="text-sm text-slate-500">({settingsForm.librarianBlocks.length})</span>
            </div>
            {!expandedSections.scheduleBlocks && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onAddBlock(); }}
                className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
              >
                + Add
              </button>
            )}
          </div>
          {expandedSections.scheduleBlocks && (
          <div className="p-6 bg-gray-50">
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={onAddBlock}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 text-sm"
              >
                + Add Block
              </button>
            </div>
            {settingsForm.librarianBlocks.length === 0 ? (
              <p className="text-slate-600 text-sm">No schedule blocks yet.</p>
            ) : (
              <div className="space-y-2">
                {settingsForm.librarianBlocks.map((block, idx) => (
                  <div key={block.id || idx} className="flex justify-between items-center bg-white p-3 rounded border border-gray-200">
                    <div className="text-sm">
                      <span className="font-semibold">{block.day}</span> {formatTime12(block.startTime)} - {formatTime12(block.endTime)}:
                      <span className="ml-2 text-gray-700">{block.reason}</span>
                      <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">{block.library}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveBlock(block.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}
        </div>

        {/* Email Notifications */}
        <div className="border-2 border-blue-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-blue-50 to-cyan-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-blue-100 hover:to-cyan-100"
            onClick={() => toggleSection('emailNotifications')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.emailNotifications ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-slate-800">Email Notifications</h3>
              <span className={`text-sm px-2 py-0.5 rounded ${settingsForm.emailEnabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                {settingsForm.emailEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
          {expandedSections.emailNotifications && (
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 px-6 pb-6">
            <div className="flex items-center justify-between mb-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <span className="text-slate-700 font-medium">Enable</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={settingsForm.emailEnabled}
                    onChange={(e) => setSettingsForm({...settingsForm, emailEnabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
                </div>
              </label>
            </div>
            {settingsForm.emailEnabled ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-700 text-sm font-medium mb-1">Your Email</label>
                  <input
                    type="email"
                    placeholder="librarian@school.edu"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    value={settingsForm.librarianEmail}
                    onChange={(e) => setSettingsForm({...settingsForm, librarianEmail: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">Teachers and you will receive email notifications for booking requests.</p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-600 italic">Email notifications are disabled.</p>
            )}
          </div>
          )}
        </div>

        {/* Recess Configuration */}
        <div className="border-2 border-amber-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-amber-50 to-yellow-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-amber-100 hover:to-yellow-100"
            onClick={() => toggleSection('recessConfig')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.recessConfig ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-amber-800">Recess Configuration</h3>
              <span className={`text-sm px-2 py-0.5 rounded ${(settingsForm.recessSchedules || []).some(r => r.enabled) ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                {(settingsForm.recessSchedules || []).filter(r => r.enabled).length > 0
                  ? `${(settingsForm.recessSchedules || []).filter(r => r.enabled).length} Active`
                  : 'None'}
              </span>
            </div>
          </div>
          {expandedSections.recessConfig && (
            <div className="p-6 bg-gradient-to-br from-amber-50 to-yellow-50">
              {/* Existing recess schedules */}
              <div className="space-y-4 mb-4">
                {(settingsForm.recessSchedules || []).map((recess, index) => {
                  const updateRecess = (field, value) => {
                    const updated = [...(settingsForm.recessSchedules || [])];
                    updated[index] = { ...updated[index], [field]: value };
                    const newForm = { ...settingsForm, recessSchedules: updated };
                    setSettingsForm(newForm);
                    autoSaveSettings(newForm);
                  };

                  return (
                    <div key={recess.id} className="bg-white rounded-lg border border-amber-200 p-4">
                      {/* Header with toggle and delete */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <label className="relative cursor-pointer">
                            <input
                              type="checkbox"
                              checked={recess.enabled}
                              onChange={(e) => updateRecess('enabled', e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                          </label>
                          <span className="font-semibold text-amber-800">{recess.description || `Recess ${index + 1}`}</span>
                          {recess.enabled && recess.grades?.length > 0 && (
                            <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                              Grades: {recess.grades.join(', ')}
                            </span>
                          )}
                        </div>
                        <button
                          onClick={() => {
                            const updated = (settingsForm.recessSchedules || []).filter((_, i) => i !== index);
                            const newForm = { ...settingsForm, recessSchedules: updated };
                            setSettingsForm(newForm);
                            autoSaveSettings(newForm);
                          }}
                          className="text-red-400 hover:text-red-600 text-sm px-2 py-1"
                          title="Remove recess"
                        >
                          Remove
                        </button>
                      </div>

                      {recess.enabled && (
                        <div className="space-y-3">
                          {/* Description */}
                          <div>
                            <label className="block text-slate-700 text-sm font-medium mb-1">Name</label>
                            <input
                              type="text"
                              placeholder="e.g., 5th Grade Recess, MS Recess"
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                              value={recess.description || ''}
                              onChange={(e) => updateRecess('description', e.target.value)}
                            />
                          </div>

                          {/* Time Pickers & Day */}
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <TimePicker
                              label="Start Time"
                              value={recess.startTime || '11:30'}
                              onChange={(val) => updateRecess('startTime', val)}
                            />
                            <TimePicker
                              label="End Time"
                              value={recess.endTime || '12:00'}
                              onChange={(val) => updateRecess('endTime', val)}
                            />
                            <div>
                              <label className="block text-slate-700 text-sm font-medium mb-1">Day of Week</label>
                              <select
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                                value={recess.dayOfWeek || 'everyday'}
                                onChange={(e) => updateRecess('dayOfWeek', e.target.value)}
                              >
                                {DAY_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Grade Selection */}
                          <div>
                            <label className="block text-slate-700 text-sm font-medium mb-2">Grades to notify</label>
                            <div className="flex flex-wrap gap-2">
                              {RECESS_GRADE_OPTIONS.map(grade => {
                                const isSelected = (recess.grades || []).includes(grade);
                                return (
                                  <label
                                    key={grade}
                                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border cursor-pointer text-sm transition-colors ${
                                      isSelected
                                        ? 'bg-amber-100 border-amber-400 text-amber-700'
                                        : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="sr-only"
                                      checked={isSelected}
                                      onChange={() => {
                                        const currentGrades = recess.grades || [];
                                        const newGrades = isSelected
                                          ? currentGrades.filter(g => g !== grade)
                                          : [...currentGrades, grade];
                                        updateRecess('grades', newGrades);
                                      }}
                                    />
                                    {grade}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Add Recess Button */}
              <button
                onClick={() => {
                  const newRecess = {
                    id: Date.now().toString(),
                    enabled: true,
                    startTime: '11:30',
                    endTime: '12:00',
                    dayOfWeek: 'everyday',
                    grades: [],
                    description: ''
                  };
                  const newForm = {
                    ...settingsForm,
                    recessSchedules: [...(settingsForm.recessSchedules || []), newRecess]
                  };
                  setSettingsForm(newForm);
                  autoSaveSettings(newForm);
                }}
                className="w-full px-4 py-3 border-2 border-dashed border-amber-300 rounded-lg text-amber-700 hover:bg-amber-50 hover:border-amber-400 transition-colors text-sm font-medium"
              >
                + Add Recess
              </button>

              {/* Info Note */}
              {(settingsForm.recessSchedules || []).length > 0 && (
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 mt-4">
                  <p className="text-sm text-amber-800">
                    When <strong>BOTH</strong> libraries close during a recess time, teachers for that recess's selected grades will be notified.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Admin Contacts */}
        <div className="border-2 border-rose-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-rose-50 to-pink-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-rose-100 hover:to-pink-100"
            onClick={() => toggleSection('adminContacts')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.adminContacts ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-rose-800">Admin Contacts</h3>
              <span className="px-2 py-0.5 bg-rose-200 text-rose-700 rounded-full text-xs font-medium">
                {(settingsForm.adminContacts || []).length}
              </span>
            </div>
          </div>
          {expandedSections.adminContacts && (
            <div className="p-6 bg-gradient-to-br from-rose-50 to-pink-50">
              {/* Add Admin Contact Form */}
              <div className="bg-white rounded-lg p-4 mb-4 border border-rose-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Name"
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    value={newAdminContact.name}
                    onChange={(e) => setNewAdminContact({ ...newAdminContact, name: e.target.value })}
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    value={newAdminContact.email}
                    onChange={(e) => setNewAdminContact({ ...newAdminContact, email: e.target.value })}
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-600">Notify on:</span>
                    {['closures', 'events', 'inventory'].map(type => (
                      <label
                        key={type}
                        className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer text-xs transition-colors ${
                          newAdminContact.notifyOn.includes(type)
                            ? 'bg-rose-100 border-rose-400 text-rose-700'
                            : 'bg-slate-50 border-slate-300 text-slate-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={newAdminContact.notifyOn.includes(type)}
                          onChange={() => {
                            const newNotifyOn = newAdminContact.notifyOn.includes(type)
                              ? newAdminContact.notifyOn.filter(t => t !== type)
                              : [...newAdminContact.notifyOn, type];
                            setNewAdminContact({ ...newAdminContact, notifyOn: newNotifyOn });
                          }}
                        />
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!newAdminContact.name || !newAdminContact.email) {
                      showNotification('Please fill in name and email', 'error');
                      return;
                    }
                    if (!isValidEmail(newAdminContact.email)) {
                      showNotification('Please enter a valid email address', 'error');
                      return;
                    }
                    if (newAdminContact.notifyOn.length === 0) {
                      showNotification('Please select at least one notification type', 'error');
                      return;
                    }
                    const newContact = { ...newAdminContact, id: Date.now().toString() };
                    const newForm = {
                      ...settingsForm,
                      adminContacts: [...(settingsForm.adminContacts || []), newContact]
                    };
                    setSettingsForm(newForm);
                    autoSaveSettings(newForm);
                    setNewAdminContact({ name: '', email: '', notifyOn: ['closures'] });
                    showNotification('Admin contact added', 'success');
                  }}
                  className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 text-sm"
                >
                  + Add Contact
                </button>
              </div>

              {/* Contact List */}
              {(settingsForm.adminContacts || []).length > 0 ? (
                <div className="space-y-2">
                  {(settingsForm.adminContacts || []).map(contact => (
                    <div key={contact.id} className="flex justify-between items-center bg-white p-3 rounded-lg border border-rose-100">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-slate-800">{contact.name}</span>
                        <span className="text-slate-500 text-sm">({contact.email})</span>
                        {(contact.notifyOn || []).map(type => (
                          <span key={type} className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded text-xs">
                            {type}
                          </span>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Remove ${contact.name} from admin contacts?`)) {
                            const newForm = {
                              ...settingsForm,
                              adminContacts: (settingsForm.adminContacts || []).filter(c => c.id !== contact.id)
                            };
                            setSettingsForm(newForm);
                            autoSaveSettings(newForm);
                            showNotification('Admin contact removed', 'success');
                          }
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-4">No admin contacts added yet.</p>
              )}

              {/* Info Note */}
              <div className="mt-4 bg-rose-100 border border-rose-300 rounded-lg p-3">
                <p className="text-sm text-rose-800">
                  These contacts will be CC'd on library notifications based on their preferences.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Library Chairs */}
        <div className="border-2 border-violet-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-violet-50 to-purple-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-violet-100 hover:to-purple-100"
            onClick={() => toggleSection('libraryChairs')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.libraryChairs ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-violet-800">Library Chairs</h3>
              <span className="px-2 py-0.5 bg-violet-200 text-violet-700 rounded-full text-xs font-medium">
                {(settingsForm.libraryChairs || []).length}
              </span>
            </div>
          </div>
          {expandedSections.libraryChairs && (
            <div className="p-6 bg-gradient-to-br from-violet-50 to-purple-50">
              {/* Add Library Chair Form */}
              <div className="bg-white rounded-lg p-4 mb-4 border border-violet-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <input
                    type="text"
                    placeholder="Name"
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    value={newLibraryChair.name}
                    onChange={(e) => setNewLibraryChair({ ...newLibraryChair, name: e.target.value })}
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    value={newLibraryChair.email}
                    onChange={(e) => setNewLibraryChair({ ...newLibraryChair, email: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!newLibraryChair.name || !newLibraryChair.email) {
                        showNotification('Please fill in name and email', 'error');
                        return;
                      }
                      if (!isValidEmail(newLibraryChair.email)) {
                        showNotification('Please enter a valid email address', 'error');
                        return;
                      }
                      const newChair = { ...newLibraryChair, id: Date.now().toString() };
                      const newForm = {
                        ...settingsForm,
                        libraryChairs: [...(settingsForm.libraryChairs || []), newChair]
                      };
                      setSettingsForm(newForm);
                      autoSaveSettings(newForm);
                      setNewLibraryChair({ name: '', email: '' });
                      showNotification('Library chair added', 'success');
                    }}
                    className="bg-violet-600 text-white px-4 py-2 rounded-lg hover:bg-violet-700 text-sm"
                  >
                    + Add Chair
                  </button>
                </div>
              </div>

              {/* Chair List */}
              {(settingsForm.libraryChairs || []).length > 0 ? (
                <div className="space-y-2 mb-4">
                  {(settingsForm.libraryChairs || []).map(chair => (
                    <div key={chair.id} className="flex justify-between items-center bg-white p-3 rounded-lg border border-violet-100">
                      <div>
                        <span className="font-medium text-slate-800">{chair.name}</span>
                        <span className="text-slate-500 text-sm ml-2">({chair.email})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Remove ${chair.name} from library chairs?`)) {
                            const newForm = {
                              ...settingsForm,
                              libraryChairs: (settingsForm.libraryChairs || []).filter(c => c.id !== chair.id)
                            };
                            setSettingsForm(newForm);
                            autoSaveSettings(newForm);
                            showNotification('Library chair removed', 'success');
                          }
                        }}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm text-center py-4 mb-4">No library chairs added yet.</p>
              )}

              {/* Divider */}
              <hr className="border-violet-200 mb-4" />

              {/* Library Chair Settings */}
              <div className="space-y-4">
                <h4 className="font-medium text-violet-800">Notification Settings</h4>

                {/* Weekly Digest */}
                <div className="bg-white rounded-lg p-4 border border-violet-100">
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <span className="text-slate-700 font-medium">Weekly Digest</span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={settingsForm.libraryChairSettings?.weeklyDigestEnabled ?? true}
                          onChange={(e) => {
                            const newForm = {
                              ...settingsForm,
                              libraryChairSettings: { ...settingsForm.libraryChairSettings, weeklyDigestEnabled: e.target.checked }
                            };
                            setSettingsForm(newForm);
                            autoSaveSettings(newForm);
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-violet-500"></div>
                      </div>
                    </label>
                  </div>
                  {settingsForm.libraryChairSettings?.weeklyDigestEnabled && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-slate-700 text-sm font-medium mb-1">Day</label>
                        <select
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                          value={settingsForm.libraryChairSettings?.weeklyDigestDay || 'Friday'}
                          onChange={(e) => {
                            const newForm = {
                              ...settingsForm,
                              libraryChairSettings: { ...settingsForm.libraryChairSettings, weeklyDigestDay: e.target.value }
                            };
                            setSettingsForm(newForm);
                            autoSaveSettings(newForm);
                          }}
                        >
                          <option value="Monday">Monday</option>
                          <option value="Tuesday">Tuesday</option>
                          <option value="Wednesday">Wednesday</option>
                          <option value="Thursday">Thursday</option>
                          <option value="Friday">Friday</option>
                          <option value="Saturday">Saturday</option>
                          <option value="Sunday">Sunday</option>
                        </select>
                      </div>
                      <TimePicker
                        label="Time"
                        value={settingsForm.libraryChairSettings?.weeklyDigestTime || '18:00'}
                        onChange={(val) => {
                          const newForm = {
                            ...settingsForm,
                            libraryChairSettings: { ...settingsForm.libraryChairSettings, weeklyDigestTime: val }
                          };
                          setSettingsForm(newForm);
                          autoSaveSettings(newForm);
                        }}
                      />
                    </div>
                  )}
                </div>

                {/* Emergency Closure */}
                <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-violet-100">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <span className="text-slate-700 font-medium">Notify on Emergency Closure</span>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={settingsForm.libraryChairSettings?.notifyOnEmergencyClosure ?? true}
                        onChange={(e) => {
                          const newForm = {
                            ...settingsForm,
                            libraryChairSettings: { ...settingsForm.libraryChairSettings, notifyOnEmergencyClosure: e.target.checked }
                          };
                          setSettingsForm(newForm);
                          autoSaveSettings(newForm);
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-violet-500"></div>
                    </div>
                  </label>
                </div>

                {/* Late Teacher Cancellations */}
                <div className="bg-white rounded-lg p-4 border border-violet-100">
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <span className="text-slate-700 font-medium">Notify on Late Teacher Cancellations</span>
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={settingsForm.libraryChairSettings?.notifyOnLateTeacherCancel ?? true}
                          onChange={(e) => {
                            const newForm = {
                              ...settingsForm,
                              libraryChairSettings: { ...settingsForm.libraryChairSettings, notifyOnLateTeacherCancel: e.target.checked }
                            };
                            setSettingsForm(newForm);
                            autoSaveSettings(newForm);
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-violet-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-violet-500"></div>
                      </div>
                    </label>
                  </div>
                  {settingsForm.libraryChairSettings?.notifyOnLateTeacherCancel && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-600">"Late" means less than</span>
                      <input
                        type="number"
                        min="1"
                        max="30"
                        className="w-16 px-2 py-1 border border-slate-300 rounded text-sm text-center"
                        value={settingsForm.libraryChairSettings?.lateNoticeDays || 7}
                        onChange={(e) => {
                          const newForm = {
                            ...settingsForm,
                            libraryChairSettings: { ...settingsForm.libraryChairSettings, lateNoticeDays: parseInt(e.target.value) || 7 }
                          };
                          setSettingsForm(newForm);
                          autoSaveSettings(newForm);
                        }}
                      />
                      <span className="text-sm text-slate-600">days notice</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Info Note */}
              <div className="mt-4 bg-violet-100 border border-violet-300 rounded-lg p-3">
                <p className="text-sm text-violet-800">
                  Library Chairs receive a weekly schedule digest and alerts when teachers cancel with short notice.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Library Assistant */}
        <div className="border-2 border-teal-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-teal-50 to-cyan-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-teal-100 hover:to-cyan-100"
            onClick={() => toggleSection('libraryAssistant')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.libraryAssistant ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-teal-800">Library Assistant</h3>
              <span className={`text-sm px-2 py-0.5 rounded ${settingsForm.libraryAssistant?.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                {settingsForm.libraryAssistant?.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
          {expandedSections.libraryAssistant && (
            <div className="p-6 bg-gradient-to-br from-teal-50 to-cyan-50">
              {/* Enable Toggle */}
              <div className="flex items-center justify-between mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <span className="text-slate-700 font-medium">Enable Library Assistant Notifications</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={settingsForm.libraryAssistant?.enabled || false}
                      onChange={(e) => {
                        const newForm = {
                          ...settingsForm,
                          libraryAssistant: { ...settingsForm.libraryAssistant, enabled: e.target.checked }
                        };
                        setSettingsForm(newForm);
                        autoSaveSettings(newForm);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-teal-500"></div>
                  </div>
                </label>
              </div>

              {settingsForm.libraryAssistant?.enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-slate-700 text-sm font-medium mb-1">Name</label>
                      <input
                        type="text"
                        placeholder="Assistant Name"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        value={settingsForm.libraryAssistant?.name || ''}
                        onChange={(e) => {
                          const newForm = {
                            ...settingsForm,
                            libraryAssistant: { ...settingsForm.libraryAssistant, name: e.target.value }
                          };
                          setSettingsForm(newForm);
                          autoSaveSettings(newForm);
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-slate-700 text-sm font-medium mb-1">Email</label>
                      <input
                        type="email"
                        placeholder="assistant@school.edu"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                        value={settingsForm.libraryAssistant?.email || ''}
                        onChange={(e) => {
                          const newForm = {
                            ...settingsForm,
                            libraryAssistant: { ...settingsForm.libraryAssistant, email: e.target.value }
                          };
                          setSettingsForm(newForm);
                          autoSaveSettings(newForm);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Info Note */}
              <div className="mt-4 bg-teal-100 border border-teal-300 rounded-lg p-3">
                <p className="text-sm text-teal-800">
                  Library Assistant is notified of <strong>full-day closures only</strong> (PD days, TLA, sick days). Not notified of partial closures or individual class cancellations.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Test Emails */}
        <div className="border-2 border-fuchsia-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-fuchsia-50 to-pink-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-fuchsia-100 hover:to-pink-100"
            onClick={() => toggleSection('testEmails')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.testEmails ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-fuchsia-800">Test Emails</h3>
            </div>
          </div>
          {expandedSections.testEmails && (
            <div className="p-6 bg-gradient-to-br from-fuchsia-50 to-pink-50">
              {!settingsForm.librarianEmail ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
                  <p className="font-medium">Configure your email first</p>
                  <p className="text-sm mt-1">Enter your email address in the "Email Notifications" section above to send test emails.</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-fuchsia-700 mb-4">
                    Test emails will be sent to: <strong>{settingsForm.librarianEmail}</strong>
                  </p>
                  <p className="text-xs text-slate-500 mb-4">
                    Click on an email type to expand its form and customize the test data before sending.
                  </p>

                  {/* Group by category */}
                  {['Closures', 'Bookings', 'Library Chairs', 'Events'].map(category => (
                    <div key={category} className="mb-6">
                      <h4 className="font-semibold text-fuchsia-800 mb-3 text-sm uppercase tracking-wide">
                        {category}
                      </h4>
                      <div className="space-y-2">
                        {TEST_EMAIL_TYPES.filter(t => t.category === category).map(emailType => {
                          const isExpanded = expandedTestEmail === emailType.id;
                          return (
                            <div
                              key={emailType.id}
                              className={`bg-white rounded-lg border transition-all ${
                                isExpanded ? 'border-fuchsia-400 shadow-md' : 'border-fuchsia-100'
                              }`}
                            >
                              {/* Header - clickable to expand/collapse */}
                              <div
                                className={`p-4 cursor-pointer hover:bg-fuchsia-50/50 transition-colors ${
                                  isExpanded ? 'border-b border-fuchsia-200' : ''
                                }`}
                                onClick={() => setExpandedTestEmail(isExpanded ? null : emailType.id)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`}>▶</span>
                                      <span className="text-xl">{emailType.icon}</span>
                                      <h5 className="font-medium text-slate-800">{emailType.name}</h5>
                                      {testEmailResults[emailType.id] === 'success' && (
                                        <span className="text-green-600 text-sm">✓ Sent</span>
                                      )}
                                      {testEmailResults[emailType.id] === 'error' && (
                                        <span className="text-red-600 text-sm">✗ Failed</span>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-500 mt-1 ml-8">{emailType.description}</p>
                                  </div>
                                </div>
                              </div>

                              {/* Expanded Form */}
                              {isExpanded && (
                                <div className="p-4 bg-fuchsia-50/30">
                                  {/* Recipients info */}
                                  <div className="mb-4 text-xs text-slate-600">
                                    <span className="font-medium">Would send to: </span>
                                    {emailType.getRecipients().map((r, i) => (
                                      <span key={i}>
                                        {i > 0 && ', '}
                                        <span className="bg-slate-100 px-1.5 py-0.5 rounded">{r}</span>
                                      </span>
                                    ))}
                                  </div>

                                  {/* Form fields */}
                                  <div className="mb-4">
                                    {emailType.renderForm && emailType.renderForm()}
                                  </div>

                                  {/* Send button */}
                                  <div className="flex justify-end">
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        sendTestEmail(emailType);
                                      }}
                                      disabled={sendingTestEmail}
                                      className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        sendingTestEmail
                                          ? 'bg-fuchsia-300 text-fuchsia-700 cursor-wait'
                                          : 'bg-fuchsia-600 text-white hover:bg-fuchsia-700'
                                      }`}
                                    >
                                      {sendingTestEmail ? 'Sending...' : 'Send Test Email'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* Send All Button */}
                  <div className="mt-6 pt-4 border-t border-fuchsia-200">
                    <button
                      type="button"
                      onClick={async () => {
                        for (const emailType of TEST_EMAIL_TYPES) {
                          await sendTestEmail(emailType);
                          // Small delay between sends to avoid rate limiting
                          await new Promise(resolve => setTimeout(resolve, 500));
                        }
                      }}
                      disabled={sendingTestEmail}
                      className="w-full bg-fuchsia-700 text-white px-4 py-3 rounded-lg hover:bg-fuchsia-800 transition-colors font-medium disabled:opacity-50"
                    >
                      Send All Test Emails
                    </button>
                    <p className="text-xs text-fuchsia-600 mt-2 text-center">
                      This will send {TEST_EMAIL_TYPES.length} test emails to {settingsForm.librarianEmail}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Teacher Directory */}
        <div className="border-2 border-emerald-200 rounded-xl overflow-hidden">
          <div
            className="bg-gradient-to-br from-emerald-50 to-green-50 px-6 py-4 flex justify-between items-center cursor-pointer hover:from-emerald-100 hover:to-green-100"
            onClick={() => toggleSection('teacherDirectory')}
          >
            <div className="flex items-center gap-2">
              <span className={`transition-transform ${expandedSections.teacherDirectory ? 'rotate-90' : ''}`}>▶</span>
              <h3 className="font-bold text-emerald-800">Teacher Directory</h3>
              <span className="px-2 py-0.5 bg-emerald-200 text-emerald-700 rounded-full text-xs font-medium">
                {teachers.length} teacher{teachers.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          {expandedSections.teacherDirectory && (
          <div className="p-6">
          <p className="text-sm text-emerald-600 mb-4">Add teachers here to quickly select them when creating fixed classes.</p>

          {/* Add Teacher Form */}
          <div className="bg-white rounded-lg p-4 mb-4 border border-emerald-100">
            {/* Row 1: Name, Email, Grade, Subject */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <input
                type="text"
                placeholder="Teacher Name"
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                value={newTeacher.name}
                onChange={(e) => setNewTeacher({...newTeacher, name: e.target.value})}
              />
              <input
                type="email"
                placeholder="Email"
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                value={newTeacher.email}
                onChange={(e) => setNewTeacher({...newTeacher, email: e.target.value})}
              />
              <select
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                value={newTeacher.grade}
                onChange={(e) => setNewTeacher({...newTeacher, grade: e.target.value, subject: ['5', '6', '7', '8'].includes(e.target.value) ? newTeacher.subject : ''})}
              >
                <option value="">Select Grade...</option>
                <option value="PreK">PreK</option>
                <option value="K">Kindergarten</option>
                <option value="1">1st Grade</option>
                <option value="2">2nd Grade</option>
                <option value="3">3rd Grade</option>
                <option value="4">4th Grade</option>
                <option value="5">5th Grade</option>
                <option value="6">6th Grade</option>
                <option value="7">7th Grade</option>
                <option value="8">8th Grade</option>
              </select>
              {['5', '6', '7', '8'].includes(newTeacher.grade) && (
                <select
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  value={newTeacher.subject}
                  onChange={(e) => setNewTeacher({...newTeacher, subject: e.target.value})}
                >
                  <option value="">Select Subject...</option>
                  <option value="English">English</option>
                  <option value="Math">Math</option>
                  <option value="Science">Science</option>
                  <option value="Social Studies">Social Studies</option>
                  <option value="Religion">Religion</option>
                  <option value="Spanish">Spanish</option>
                  <option value="Art">Art</option>
                  <option value="Music">Music</option>
                  <option value="PE">PE</option>
                  <option value="Other">Other</option>
                </select>
              )}
            </div>

            {/* Row 2: Role and Departments */}
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-600 font-medium">Role:</label>
                <select
                  className="px-3 py-2 border border-slate-300 rounded-lg text-sm"
                  value={newTeacher.role}
                  onChange={(e) => setNewTeacher({...newTeacher, role: e.target.value})}
                >
                  {ROLE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-600 font-medium">Departments:</label>
                <div className="flex flex-wrap gap-2">
                  {DEPARTMENT_OPTIONS.map(dept => (
                    <label
                      key={dept.value}
                      className={`flex items-center gap-1 px-2 py-1 rounded-lg border cursor-pointer text-sm transition-colors ${
                        newTeacher.departments.includes(dept.value)
                          ? 'bg-emerald-100 border-emerald-400 text-emerald-700'
                          : 'bg-slate-50 border-slate-300 text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={newTeacher.departments.includes(dept.value)}
                        onChange={() => toggleDepartment(dept.value)}
                      />
                      {dept.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (!newTeacher.name || !newTeacher.email) {
                  showNotification('Please fill in name and email', 'error');
                  return;
                }
                onAddTeacher(newTeacher);
                setNewTeacher({ name: '', email: '', grade: '', subject: '', role: 'teacher', departments: [] });
              }}
              className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 text-sm"
            >
              + Add Teacher
            </button>
          </div>

          {/* Teacher List */}
          {teachers.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {teachers.sort((a, b) => a.name.localeCompare(b.name)).map(teacher => (
                <div key={teacher.id} className="flex justify-between items-center bg-white p-3 rounded-lg border border-emerald-100">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="font-medium text-slate-800">{teacher.name}</span>
                    <span className="text-slate-500 text-sm">({teacher.email})</span>
                    {/* Role tag */}
                    {teacher.role && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        teacher.role === 'teacher' ? 'bg-emerald-100 text-emerald-700' :
                        teacher.role === 'aide' ? 'bg-blue-100 text-blue-700' :
                        teacher.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                        teacher.role === 'specialist' ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {ROLE_OPTIONS.find(r => r.value === teacher.role)?.label || teacher.role}
                      </span>
                    )}
                    {/* Grade tag */}
                    {teacher.grade && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        {teacher.grade === 'K' ? 'Kindergarten' : teacher.grade === 'PreK' ? 'PreK' : `${teacher.grade}${['1','2','3'].includes(teacher.grade) ? (['1'].includes(teacher.grade) ? 'st' : ['2'].includes(teacher.grade) ? 'nd' : 'rd') : 'th'} Grade`}
                      </span>
                    )}
                    {/* Subject tag */}
                    {teacher.subject && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{teacher.subject}</span>
                    )}
                    {/* Department tags */}
                    {teacher.departments?.map(dept => (
                      <span key={dept} className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs">
                        {DEPARTMENT_OPTIONS.find(d => d.value === dept)?.label || dept}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`Remove ${teacher.name} from the directory?`)) {
                        onDeleteTeacher(teacher.id);
                      }
                    }}
                    className="text-red-500 hover:text-red-700 text-sm shrink-0"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm text-center py-4">No teachers added yet. Add teachers above to use them in fixed classes.</p>
          )}
          </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => window.open('/calendar', '_blank')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-medium text-lg"
          >
            Open Teacher Portal →
          </button>
        </div>
      </form>
    </div>
  );
}

export default SettingsTab;
